<template>
    <div class="mb-5">
        <div class="card">
            <div class="card-body">
                <h4 class="card-title">
                    <span class="mdi mdi-home-outline text-muted mr-1"></span> {{ formattedAddress }}
                </h4>
                <hr>

                <div class="row mt-4">
                    <div class="col">
                        <div class="header-info-item">
                            <label>
                                <span class="mdi mdi-account-badge-horizontal-outline"></span>
                                Surveyor
                            </label>
                            {{ report.surveyor.name }}
                        </div>

                        <div class="header-info-item">
                            <label>
                                <span class="mdi mdi-clock-outline text-muted mr-1"></span>
                                Date Scheduled
                            </label>                            
                            {{ $moment(report.date_scheduled).format('dddd, MMMM Do YYYY') }}
                        </div>
                    </div>
                    <div class="col">
                        <div class="header-info-item">
                            <label>
                                <span class="mdi mdi-account-circle-outline"></span>
                                Client Name
                            </label>
                            {{ report.client_details.name }}
                        </div>

                        <div class="header-info-item">
                            <label>
                                <span class="mdi mdi-phone-classic"></span> Client Contact                                
                            </label>
                            {{ formattedClientContact }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Published Report -->
        <div class="card mt-3">
            <div class="card-header">
                <div class="card-header-title">
                    <span class="mdi mdi-web text-muted mr-1"></span> Published Report
                </div>
            </div>
            <div class="card-body">
                <div class="alert alert-danger" v-if="publishError">
                    {{ publishError }}
                </div>

                <div class="text-center mb-4" v-if="!lastPublishRecord">                    
                    <h4 class="text-muted mt-2" v-if="!isPublishing">
                        The Customer Report has not been created yet. Click 'Preview Report' to get started
                    </h4>

                    <!-- Show if publishing -->
                    <h4 class="text-muted mt-2" v-if="isPublishing">
                        {{ isPublishingMessage }} <span class="mdi mdi-loading mdi-spin ms-4"></span>
                    </h4>

                    <button class="btn btn-primary mt-2" @click="buildReport('Draft')" :disabled="isPublishing">
                        <span class="mdi mdi-web"></span> 
                        <!-- <span class="mdi mdi-loading mdi-spin" v-if="isPublishing"></span> -->
                        Preview Report
                    </button>
                </div>

                <div v-if="lastPublishRecord">
                    <div class="row" style="font-size: 1.2em;">
                        <div class="col">                            
                            <!-- Show if publishing -->
                            <h4 class="text-center text-muted mt-2" v-if="isPublishing">
                                {{ isPublishingMessage }} <span class="mdi mdi-loading mdi-spin ms-4"></span>
                            </h4>

                            <div v-if="!isPublishing">
                                <p>
                                    <a :href="websiteUrl" target="_blank">
                                        <span class="mdi mdi-web"></span>
                                        {{ report.job_reference}} Website ({{ lastPublishRecord.publish_status }}) (Click to open) 
                                        <span class="mdi mdi-link-variant"></span>
                                    </a>
                                    <span class="text-muted ms-2">
                                        (Created: {{ $moment(lastPublishRecord.publish_date).format('dddd MMMM Do YYYY @ HH:mm') }})
                                    </span>
                                </p>
    
                                <div class="mt-1" v-if="lastPublishRecord.pdf_access_url">
                                    <p>
                                        <a :href="lastPublishRecord.pdf_access_url" target="_blank">
                                            <span class="mdi mdi-file-pdf-outline text-danger" ></span>
                                            {{ report.job_reference }} PDF Report ({{ lastPublishRecord.publish_status }})
                                            <span class="mdi mdi-file-download-outline"></span>
                                        </a>
                                        <span class="text-muted ms-2">
                                        (Created: {{ $moment(lastPublishRecord.pdf_publish_date).format('dddd MMMM Do YYYY @ HH:mm') }})
                                    </span>
                                    </p>
                                </div>
                            </div>
                        </div>                      
                    </div>
                    <hr>

                    <div class="row">
                        <div class="col">
                            <!-- <p class="mb-2 fs-2">Preview this report for the web and PDF</p> -->
                            <button 
                                class="btn btn-primary" 
                                :disabled="isPublishing"
                                v-if="lastPublishRecord.publish_status == 'Draft'"
                                @click="buildReport('Draft')">
                                <span class="mdi mdi-web" v-if="!isPublishing"></span> 
                                <span class="mdi mdi-loading mdi-spin" v-if="isPublishing"></span>
                                Preview Report
                            </button>

                        </div>
                        <div class="col">
                            <button 
                                class="btn btn-success ml-2 float-right" 
                                :disabled="isPublishing || lastPublishRecord.publish_status == 'Published'"
                                @click="showSignReport()">
                                <span class="mdi mdi-check-all" v-if="!isPublishing"></span>
                                <span class="mdi mdi-loading mdi-spin" v-if="isPublishing"></span>
                                Publish Report
                            </button>
                        </div>
                    </div> 
                </div>
            </div>
        </div>

        <!-- Cover image -->
        <div class="card mt-3">
            <div class="card-header">
                <div class="card-header-title">
                    <span class="mdi mdi-image-multiple-outline text-muted mr-1"></span> Cover Image
                </div>
            </div>
            <div class="card-body">
                <div v-if="coverImageMedia">
                    <img :src="coverImageMedia.thumbnail_access_url || coverImageMedia.access_url" style="max-width: 40%;">
                </div>
                <div class="text-center mb-4" v-if="!isLoadingCoverImage && !coverImageMedia">
                    <span class="mdi mdi-image-multiple-outline" style="font-size: 3em; opacity: 0.2;"></span> 
                    <h5 class="mt-0 text-muted">Cover image for this report has not been set</h5>
                </div>
            </div>
        </div>

        <!-- Word Exports -->
        <div class="card mt-3">
            <div class="card-header">
                <div class="card-header-title">
                    <span class="mdi mdi-attachment mdi-rotate-45 text-muted mr-1"></span> Word Exports
                </div>
            </div>
            <div class="card-body">                
                <div v-if="!loadingExports">
                    <div class="alert alert-danger" v-if="exportsLoadError">
                        Error loading exports for this report
                    </div>

                    <div class="text-center mb-4" v-if="!exports || exports.length == 0">
                        <span class="mdi mdi-cloud-download-outline" style="font-size: 3em; opacity: 0.2;"></span> 
                        <h5 class="mt-0 text-muted">No exports found for this report</h5>
                    </div>

                    <div class="row" style="font-size: 1.2em;" v-for="(doc, idx) in exports" :key="idx">
                        <div class="col">
                            <span class="mdi mdi-file-word-outline text-primary mr-2" v-if="doc.file_type == 'docx'"></span>
                            <span class="mdi mdi-file-pdf-outline text-danger mr-2" v-if="doc.file_type == 'pdf'"></span>
                            <a :href="doc.access_url" target="_blank">{{ doc.name }}</a>
                        </div>
                        <div class="col">
                            <p class="text-muted mb-0 float-right">
                                {{ $moment(doc.lastModified).format('ddd, MMMM Do YYYY, HH:mm') }}
                            </p>   
                        </div>                        
                    </div>
                </div>               

                <LoadingIndicator message="Loading exports" wrapperClass="mt-2 mb-3" v-if="loadingExports && !exportsLoadError" />
                <hr>

                <div class="alert alert-danger" v-if="exportError">
                    {{ exportError }}
                </div>

                <div class="row">
                    <div class="col">
                        <p class="mb-2">Export this report to a Word (DOCX) file</p>
                        <button class="btn btn-primary" @click="exportDocx" :disabled="isExporting">
                            <span class="mdi mdi-cloud-download-outline" v-if="!isExporting"></span> 
                            <span class="mdi mdi-loading mdi-spin" v-if="isExporting"></span>
                            Generate Export
                        </button>

                        <span class="ml-2" v-if="isExporting">
                            <span class="mdi mdi-loading mdi-spin" v-if="isExporting"></span>
                            Exporting document (this may take a few minutes)
                        </span>
                    </div>
                </div>               

                <div class="mt-3" v-if="exportCompleted">
                    Export completed successfully. If your download doesn't open, <a :href="exportUrl" target="_blank">click here</a>                    
                </div>
            </div>
        </div>

        <a href="" target="_blank" ref="hiddenExportLink" style="display: none;">
            {hiddenExportLink}
        </a>

        <b-modal
            id="modal-sign-report"
            title="Surveyor Signature"
            ok-title="Publish Report"
            @ok="onSignReportOK()">
            <p>This will create the final Customer Report. Please enter your name and any qualifications you would like to include and then click "Publish Report" to continue</p>
            <p class="text-danger">Warning: you will no longer be able to edit this report after it is published</p>
            <div class="form-group">
                <label for="surveyorSignature" class="control-label">Surveyor Signature</label>
                <input type="text" class="form-control" v-model="surveyorSignature">
            </div>
        </b-modal>
    </div>
</template>

<script>
    import axios from 'axios'    
    import _ from 'lodash'
    import ReportService from '../../services/reports/reportService'

    export default {
        props: {
            report: {
                type: Object, 
                default: null
            }
        },
        data() {
            return {
                exports: [],
                isExporting: false,
                loadingExports: false,
                exportsLoadError: null,
                exportCompleted: false,
                exportUrl: '',
                exportError: null,

                lastPublishRecord: null,
                isPublishing: false,
                isPublishingMessage: '',
                websiteUrl: '#',
                publishError: null,
                publishRecordsLoadError: null,
                surveyorSignature: '',

                isLoadingCoverImage: false,
                coverImageMedia: null,
                //coverImageId: null,
                //coverImageUrl: null
            }
        },
        computed: {
            formattedAddress() {
                let addr1 = this.report.property.address_1 ? `${this.report.property.address_1}, ` : ''
                let addr2 = this.report.property.address_2 ? `${this.report.property.address_2}, ` : ''
                let addr3 = this.report.property.address_3 ? `${this.report.property.address_3}, ` : ''
                let city = this.report.property.city ? `${this.report.property.city}, ` : ''
                let postcode = this.report.property.postcode ? `${this.report.property.postcode}` : ''

                return `${addr1}${addr2}${addr3}${city}${postcode}`
            },
            formattedClientContact() {
                let client = this.report.client_details
                let phone = client.mobile_no || client.telephone
                let email = client.email ? `${phone ? '/' : ''} ${client.email}` : ''

                return `${phone ? phone : ''}${email}`
            },
            reportWebsiteUrl() {
                let baseUrl = 'https://surveyplanner-client-report-site.azurewebsites.net/reports'
                let accessToken = this.lastPublishRecord && this.lastPublishRecord.website_access_token
                return `${baseUrl}/${this.report.job_reference}?accessToken=${accessToken}`
            }
        },
        async mounted() {
            await this.loadExports()
            await this.loadPublishRecord()
            await this.loadCoverImage()
        },
        methods: {
            async loadExports() {
                try {
                    this.loadingExports = true
                    let exportsResponse = await axios.get(`/api/reports/${this.report.job_reference}/exports`)

                    this.exports = exportsResponse.data.map(ex => {
                        return {
                            file_type: ex.name.split('.').pop(),
                            ...ex
                        }
                    })

                } catch (err) {
                    this.exportsLoadError = err
                }

                this.loadingExports = false
            },
            async loadPublishRecord() {
                try {
                    let response = await axios.get(`/api/reports/${this.report.job_reference}/publish-record`)
                    this.lastPublishRecord = response.data

                    let baseUrl = 'https://surveyplanner-client-report-site.azurewebsites.net/reports'
                    let websiteAccessToken = this.lastPublishRecord.website_access_token
                    this.websiteUrl = `${baseUrl}/${this.report.job_reference}?accessToken=${websiteAccessToken}`
                    
                } catch (err) {
                    if (!err.response || err.response.status != 404) {
                        this.publishRecordsLoadError = err
                    }
                }
            },
            async loadCoverImage() {
                try {
                    this.isLoadingCoverImage = true

                    let reportBaseUrl = `/api/reports/${this.report.job_reference}`
                    let reportResponse = await axios.get(`${reportBaseUrl}/header`)
                    let reportHeader = reportResponse.data

                    let coverImageId = reportHeader.cover_media_id
                    if (coverImageId) {
                        for (const section of this.report.sections) {
                            if (section.media && section.media.find(m => m.media_id == coverImageId)) {
                                let mediaUrl = `${reportBaseUrl}/sections/${section.number}/media/${coverImageId}`
                                let mediaResponse = await axios.get(mediaUrl)
                                this.coverImageMedia = mediaResponse.data
                            }
                        }
                    }

                } catch (err) {
                    
                }

                this.isLoadingCoverImage = false
            },
            showSignReport() {
                this.surveyorSignature = this.report.surveyor && this.report.surveyor.name
                this.$bvModal.show('modal-sign-report')
            },
            async onSignReportOK(bvEvt) {
                await this.buildReport('Published')
            },
            async buildReport(publishStatus) {
                let didPublishWebsite = false                
                this.publishError = null
                this.isPublishing = true                
                
                // Publish website
                try {
                    this.isPublishingMessage = 'Creating Report Website, please wait '
                    await axios.post(`/api/reports/${this.report.job_reference}/publish-website`, {
                        publish_status: publishStatus
                    })

                    await this.loadPublishRecord()
                    
                    this.$bvToast.toast('Report Website Created', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })

                    didPublishWebsite = true
                    
                } catch (err) {
                    console.error(err.response)
                    let msg = err.response && err.response.data ? ` (${err.response.data})` : ''
                    this.publishError = `Error while creating website: ${msg}`
                }

                if (didPublishWebsite) {
                    try {
                        let payload = {}
                        if (publishStatus == 'Published') {
                            payload.include_signature = true
                            payload.surveyor_signature = this.surveyorSignature
                        }

                        this.isPublishingMessage = 'Creating Report PDF, please wait '                        
                        await axios.post(`/api/reports/${this.report.job_reference}/publish-pdf`, payload)

                        this.$bvToast.toast('Report PDF Created', {
                            title: 'Success',
                            variant: 'success',
                            solid: true,
                            appendToast: true,
                            autoHideDelay: 3000
                        })

                    } catch (err) {
                        console.error(err.response)
                        let msg = err.response && err.response.data ? ` (${err.response.data})` : ''
                        this.publishError = `Error while creating PDF: ${msg}`
                    }
                }

                await this.loadPublishRecord()
                this.isPublishing = false
            },
            async exportDocx() {                
                try {
                    this.exportError = null
                    this.exportCompleted = false
                    this.isExporting = true

                    let exportUrl = `/api/reports/${this.report.job_reference}/exports`
                    let result = await axios.post(exportUrl, { export_type: 'docx' })
                    let exportData = result.data
                    let reportUrl = exportData.access_url

                    this.exportCompleted = true
                    this.exportUrl = reportUrl

                    if (this.exports.length > 0) {
                        this.exports.splice(0, 1, exportData)
                    }
                    else {
                        this.exports.push(exportData)
                    }

                    //this.$refs.hiddenExportLink.href = reportUrl
                    //this.$refs.hiddenExportLink.click()
                    window.location.href = reportUrl

                } catch (err) {
                    console.error(err.response)
                    let msg = err.response && err.response.data ? ` (${err.response.data})` : ''
                    this.exportError = `Couldn't export to DOCX ${msg}`
                }

                this.isExporting = false
            }
        }
    }
</script>

<style lang="scss">
    .header-info-item {
        label {
            margin-bottom: 0;
            font-weight: 400;
            display: block;

            .mdi {
                color: #333;
                margin-right: 4px;
            }
        }
        
        font-size: 1.2em;
        margin-bottom: 1.4em;
    }
</style>